import React, { useCallback, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import moment from 'moment'
import styled from 'styled-components'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import Dropdown from 'components/Dropdown'

import useRemoteConfigSessions from 'hooks/useRemoteConfigSessions'

import { customerDisplayName } from 'helpers/formatters'
import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

type Props = {
  sendEventCallback?: (action: string) => void
}

const RemoteConnections = ({ sendEventCallback = () => {} }: Props) => {
  const { sessions } = useRemoteConfigSessions()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)

  const onCloseDropdownCallback = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
    if (event && buttonRef.current?.contains(event.target as Node)) {
      return
    }
    setIsDropdownOpen(false)
  }, [])

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((prevState) => !prevState)
  }, [])

  if (!sessions?.length) {
    return <></>
  }

  return (
    <div>
      <S.Container ref={buttonRef}>
        <Button
          decoration="secondary"
          aria-label="remote connections"
          onPress={onDropdownToggle}
          styles={{
            button: {
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 10,
              borderRadius: 12,
              backgroundColor: color('paneBackground'),
              borderColor: color('border'),
              height: 39
            }
          }}
        >
          <S.RemoteIcon src={'/img/remote-icon.png'} alt="Remote Connections" />
          <S.ButtonText>Remote Connections</S.ButtonText>
          <S.DropdownCaret isDropdownOpen={isDropdownOpen} />
        </Button>
      </S.Container>

      <S.Dropdown
        isOpen={isDropdownOpen}
        onClose={onCloseDropdownCallback}
      >
        <S.Devices>
          {sessions?.map((session) => (
            <S.Device
            key={session.remoteAccess.requestId}
            onClick={() => sendEventCallback(`Click DRC Session ${session.device.name || session.device.deviceId}`)}
            >
              <S.deviceInfo>
                <S.CustomerName>{customerDisplayName(session.customer.firstName, session.customer.lastName, session.customer.companyName)}</S.CustomerName>
                <S.NavLink to={`/customers/${session.customer.id}/systems/${session.device.deviceId}`}>{session.device.name || session.device.deviceId}</S.NavLink>
                <S.deviceId>AUID: {session.device.deviceId}</S.deviceId>
              </S.deviceInfo>
              <S.SessionDetails>
                <S.State>{session.remoteAccess.state}</S.State>
                <S.Expiration>Expires in {moment.duration(moment(session.remoteAccess.expiresAt).diff(moment())).humanize()}</S.Expiration>
              </S.SessionDetails>
            </S.Device>
          ))}
        </S.Devices>
      </S.Dropdown>
    </div>
  )
}

export default RemoteConnections

const S = {
  Container: styled.div`
  @media (${screenSize.small}) {
    & > button {
      padding: 2px 8px;
      height: 34px !important;
      gap: 5px !important;
    }
  }
  `,
  Dropdown: styled(Dropdown)`
    width: auto;
    height: 230px;
    z-index: 1200;
    max-width: 60%;
    background: ${color('paneBackground')};
    border: 2px solid ${color('paneBackground')};
    border-radius: 16px;
    padding: 25px;

    @media (${screenSize.small}) {
      max-width: 96%;
      margin-right: 10px;
      height: auto;
    }
    
    @media (${screenSize.medium}) {
      max-width: 96%;
      margin-right: 10px;
    }
  `,
  DropdownCaret: styled(PlayArrowRoundedIcon).withConfig({
    shouldForwardProp: (prop) => !['isDropdownOpen'].includes(prop)
  }) <{ isDropdownOpen: boolean }>`
    color: ${color('actionableIcon')};
    transform: ${({ isDropdownOpen }) => !isDropdownOpen ? 'rotate(90deg);' : 'rotate(270deg);'}
  `,
  ButtonText: styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${color('primaryText')};

    @media ${screenSize.small} {
      font-size: 12px;
    }
  `,
  RemoteIcon: styled.img`
    width: 20px;
    height: 20px;
  `,
  Devices: styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  Device: styled.div`
    display: inline-block;
    border: 1px solid ${color('remoteDevicesBorder')};
    border-radius: 16px;
    width: 250px;
    padding: 10px;
  `,
  deviceInfo: styled.div`
    display: flex;
    gap: 5px;
    flex-direction: column;
    padding: 10px 5px;
  `,
  SessionDetails: styled.div`
    border-top: 1px solid ${color('border')};
    padding: 10px 0px;
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
  `,
  State: styled.div`
    text-transform: capitalize;
    font-weight: bold;
  `,
  Expiration: styled.div`
    float: right;
    color: ${color('remoteConfigExpireText')};
    font-style: italic;
  `,
  CustomerName: styled.div`
    color: ${color('primaryText')};
    font-weight: 600;
  `,
  deviceId: styled.div`
    color: ${color('primaryText')};
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    font-family: Inter, sans-serif;
  `,
  NavLink: styled(NavLink)`
    font-size: 14px;
    font-weight: 600;
    font-family: Inter, sans-serif;
  `
}
